import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {PointsService} from '../../../core/services/points.service';
import {DevicesService} from '../../../core/services/devices.service';
import {Device, DeviceId, DeviceType} from '../../../shared/models/device';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {BulkModalService} from '@features/tile-bulk-operations/shared/services/bulk-modal.service';
import {FeatureFlagsService, FeatureFlag} from '../../../core/services/feature-flags.service';
import {Option, OptionGroup, OptionGroupMultiSelect} from '../../../shared/models/option';
import {BehaviorSubject, Subscription} from 'rxjs';
import {SortTable} from '../../../shared/models/sort-table/types';
import {
  SortTableCellButtons,
  SortTableCellIcon,
  SortTableCellTextPill,
  SortTableCellTitleDescription,
} from '../../../shared/models/sort-table/cell-classes';
import * as bacnet from '../../../shared/models/bacnet';
import {makeTileFromBacnetPoint} from '../../../shared/models/bacnet';
import {
  TILE_TYPE_SIMPLE_ANALOG,
  TILE_TYPE_SIMPLE_BOOL,
  TILE_TYPE_SIMPLE_MULTISTATE,
  TileFormat,
  TileId,
  TileType,
} from '../../../shared/models/tile';
import {TilesService} from '../../../core/services/tiles.service';
import {MetadataService} from '../../../core/services/metadata.service';
import {map} from 'rxjs/operators';
import {BacnetPointSelectionCallback, BacnetPointSupertype, BacnetPoint, tileFormatToBacnetSupertypeMap} from '../../../shared/models/point';
import {UrlService} from '../../../core/services/url.service';
import {DrawerGapFromTopComponent} from '../drawer-gap-from-top/drawer-gap-from-top.component';
import {MultichoiceHorizontalComponent} from '../multichoice-horizontal/multichoice-horizontal.component';
import {PillComponent} from '../pill/pill.component';
import {SpinnerComponent} from '../spinner/spinner.component';
import {NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {SortTableComponent} from '../sort-table/sort-table.component';
import {TileEditorComponent} from '../tile-editor/tile-editor.component';
import {PopoverMultiSelectComponent} from '../popover-multi-select/popover-multi-select.component';
import {MenuButtonComponent} from '../menu-button/menu-button.component';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';
import { EMPTY_ID } from 'src/shared/constants/empty-id';
import * as Papa from 'papaparse';
import { FileDownloadUtil } from '../../../shared/utils/file-download.util';
import {__classPrivateFieldGet} from 'tslib';
import { IconGuesserUtil } from '../../../shared/utils/icon-guesser.util';
import {isMobileDevice} from '../../../shared/utils/device-utils';

@Component({
  selector: 'motn-bacnet-point-discovery',
  templateUrl: './bacnet-point-discovery.component.html',
  styleUrls: ['./bacnet-point-discovery.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerGapFromTopComponent,
    MultichoiceHorizontalComponent,
    SpinnerComponent,
    NgClass,
    FormsModule,
    SortTableComponent,
    TileEditorComponent,
    PopoverMultiSelectComponent,
    MenuButtonComponent,
    PillComponent,
  ],
})
export class BacnetPointDiscoveryComponent implements OnInit, OnDestroy {
  INFO_TIMEOUT = 'The timeout setting defines the amount of time before the Motion Hub will stop waiting for a response from a device when attempting point discovery. Some devices require more time than others to return their sets of discoverable points due to factors such as point quantity and link speed. Use this timeout setting to accommodate those cases.';
  INFO_INTERRUPTIONS = 'BACnet point discovery is data-intensive and has the potential to block or inhibit communication on BACnet networks. These inherent protocol limitations can prevent alarms, tiles, and other functionalities that depend on the Motion Hub that is performing the discovery to be blocked until point discovery is finished.';
  INFO_DATA_TYPE = 'The discovery results shown below are only the ones compatible with the type of tile chosen in the previous view.\n\nTo see points of a different type, either close this view and choose a different tile type or navigate to the Points view to view discovery results for all point types.';

  @Input() useCase: string = '';
  @Input() formatRestriction: TileFormat;
  /**
   * If customCallback is defined, the default tile creation methods in this class
   * won't be used when the user selects a point from which to create a new tile.
   */
  @Input() customCallback: BacnetPointSelectionCallback;
  @Input() preselectedDevice: DeviceId;
  /** When true, hides discovery controls and replaces "Make Tile" with a direct "Select" action. */
  @Input() selectionMode = false;

  @Output() clickedOutside$: EventEmitter<null> = new EventEmitter<null>();

  devices: Device<DeviceType.BACnet>[] = [];
  timeout: number;
  selectedDevice: DeviceId;
  discovering: boolean = false;
  filteredPoints: BacnetPoint[] = [];
  points: BacnetPoint[] = [];
  searchTerm: string = '';
  editingTile: TileId = EMPTY_ID;

  private discoverySub: Subscription;

  table$ = new BehaviorSubject<SortTable>(null);

  deviceMenuOptions: OptionGroupMultiSelect;


  constructor(
    private devicesService: DevicesService,
    private pointsService: PointsService,
    private tilesService: TilesService,
    private metadataService: MetadataService,
    private shadeTools: ShadeToolsService,
    private urlService: UrlService,
    private bulkModalService: BulkModalService,
    private featureFlagsService: FeatureFlagsService,
  ) {
  }


  ngOnInit(): void {
    this.devicesService.fetchUserDevices$().pipe(
      map((d) => {
        return d.filter(d => d.Type === 'bacnet');
      }),
    ).subscribe(
      (devices) => {
        this.devices = <Device<DeviceType.BACnet>[]>devices;

        if (!this.preselectedDevice) {
          if (this.getLastDiscoveredDevice()?.ID) {
            this.selectedDevice = this.getLastDiscoveredDevice()?.ID;
            this.onDeviceChange();
          }
        }
      },
      () => {
        this.shadeTools.errorDialog();
      },
    );

    if (this.preselectedDevice) {
      this.selectedDevice = this.preselectedDevice;
      this.onDeviceChange();
    }
  }


  ngOnDestroy() {
    this.discoverySub?.unsubscribe();
    this.stopDiscovery();
  }


  getCurrentSelectedDevice(): Device {
    return this.devices.find(d => d.ID === this.selectedDevice);
  }


  /**
   *
   */
  getLastDiscoveredDevice(): Device {
    return this.devices.find(d => d.ID === this.pointsService.lastDeviceDiscovered);
  }


  /**
   *
   */
  onDeviceChange(): void {
    if (this.pointsService.discoveryCache[this.selectedDevice]) {
      this.points = this.pointsService.discoveryCache[this.selectedDevice];
    } else {
      this.points = [];
    }

    this.filterAndRenderPoints();
  }


  onDeviceMenuClick() {
    if (this.selectionMode) return;
    const options: Option[] = [];

    for (let device of this.devices) {
      options.push({
        id: '' + device.ID,
        name: device.Name,
        selected: this.selectedDevice === device.ID,
        callback: () => {
          this.selectedDevice = device.ID;
        },
        pill: {
          letter: 'd',
          text: '' + device.ID,
        },
      });
    }

    this.deviceMenuOptions = new OptionGroupMultiSelect(options);
  }


  onDeviceMenuDone() {
    this.deviceMenuOptions = undefined;
  }


  /**
   *
   */
  infoDevicesMenu(): void {
    this.shadeTools.startDialog(
      `<i class="fa-light fa-face-monocle"></i> Don't See Your Device?`,
      `Only the BACnet/IP devices you've already added or which another user has shared with you appear here.`,
      [
        {
          text: 'OK',
          callback: () => {
          },
          color: DialogBoxButtonColor.Default,
        },
      ],
    );
  }


  /**
   *
   */
  filterAndRenderPoints(): void {
    let filteredPoints: BacnetPoint[];

    if (this.searchTerm.trim() === '') {
      filteredPoints = this.points;
    } else {
      const searchTerm = this.searchTerm.toLowerCase();

      filteredPoints = this.points.filter((point) => {
        return point.Name.toLowerCase().indexOf(searchTerm) > -1
          || point.Description.toLowerCase().indexOf(searchTerm) > -1;
      });
    }

    filteredPoints = filteredPoints.sort((a, b) => {
      if (a.Type === b.Type) {
        return +(a.Address) < +(b.Address) ? -1 : 1;
      }

      return a.Type < b.Type ? -1 : 1;
    });

    if (this.formatRestriction) {
      const supertype: BacnetPointSupertype = tileFormatToBacnetSupertypeMap[this.formatRestriction];

      filteredPoints = filteredPoints.filter((p) => {
        return p.Type.toLowerCase().indexOf(supertype) === 0;
      });
    }

    this.filteredPoints = filteredPoints;
    this.updateTable();
  }


  /**
   *
   * @private
   */
  private updateTable() {
    this.urlService.setPageNumber(1);
    const table: SortTable = {
      css: {
        'min-width': '730px',
      },
      columns: [
        {
          name: '',
          css: {
            'width': '50px',
            'justify-content': 'center',
          },
        },
        {
          name: 'Point Name',
          css: {
            'width': 'calc(100% - 670px)',
            'min-width': '120px',
          },
          sortingFn: SortTableCellTitleDescription.sortingFnTitle,
        },
        {
          name: '',
          css: {
            'width': '120px',
            'justify-content': 'right',
          },
        },
        {
          name: 'Value',
          css: {
            'width': '150px',
            'justify-content': 'center',
          },
        },
        {
          name: 'Units',
          css: {
            'width': '140px',
            'justify-content': 'center',
          },
          sortingFn: SortTableCellTextPill.sortingFnMainText,
        },
        {
          name: 'Instance',
          css: {
            'width': '80px',
            'justify-content': 'center',
          },
          sortingFn: SortTableCellTextPill.sortingFnMainText,
        },
        {
          name: 'Type',
          css: {
            'width': '130px',
            'justify-content': 'center',
          },
          sortingFn: SortTableCellTextPill.sortingFnMainText,
        },
      ],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 50,
        startOnPage: 1,
        maxPageOptions: 10,
      },
    };

    for (let point of this.filteredPoints) {
      const makeTileOptions: Option[] = [];
      point.ID = JSON.stringify([point.Type, point.Address]);

      if (this.customCallback) {
        makeTileOptions.push({
          id: 'custom',
          name: 'Create tile',
          callback: () => {
            this.customCallback(point, this.devices.find(d => d.ID === this.selectedDevice));
          },
        });
      } else {
        if (point.Type.indexOf('analog') > -1) {
          makeTileOptions.push(
            {
              id: 'numeric',
              name: 'Create numeric tile (recommended)',
              callback: () => {
                this.createTileCallback(point, TILE_TYPE_SIMPLE_ANALOG);
              },
            },
            {
              id: 'multichoice',
              name: 'Create multi-choice tile',
              callback: () => {
                this.createTileCallback(point, TILE_TYPE_SIMPLE_MULTISTATE);
              },
            },
          );
        } else if (point.Type.indexOf('binary') > -1) {
          makeTileOptions.push(
            {
              id: 'toggle',
              name: 'Create toggle tile',
              callback: () => {
                this.createTileCallback(point, TILE_TYPE_SIMPLE_BOOL);
              },
            },
          );
        } else if (point.Type.indexOf('multi') > -1) {
          makeTileOptions.push(
            {
              id: 'multichoice',
              name: 'Create multi-choice tile (recommended)',
              callback: () => {
                this.createTileCallback(point, TILE_TYPE_SIMPLE_MULTISTATE);
              },
            },
            {
              id: 'numeric',
              name: 'Create numeric tile',
              callback: () => {
                this.createTileCallback(point, TILE_TYPE_SIMPLE_ANALOG);
              },
            },
          );
        }
      }

      table.rows.push({
        context: point.ID,
        cells: [
          new SortTableCellIcon(IconGuesserUtil.guessIcon(point)),
          new SortTableCellTitleDescription(point.Name, point.Description),
          new SortTableCellButtons([
            this.selectionMode && this.customCallback
              ? {
                  id: point.ID,
                  text: 'Select',
                  callback: () => this.customCallback(point, this.devices.find(d => d.ID === this.selectedDevice)),
                  colors: ['white', 'indigo'],
                  size: 'small',
                }
              : {
                  id: point.ID,
                  text: 'Make Tile',
                  menu: new OptionGroup(makeTileOptions),
                  colors: ['white', 'indigo'],
                  size: 'small',
                },
          ]),
          new SortTableCellTextPill(bacnet.makeFriendlyPointValue(point), ''),
          new SortTableCellTextPill(this.getUnitsString(point.UnitsState), ''),
          new SortTableCellTextPill(point.Address, ''),
          new SortTableCellTextPill(point.Type, ''),
        ],
      });
    }

    this.table$.next(table);
  }


  /**
   * This is called as long as customCallback isn't defined.
   *
   * @param point
   * @param tileType
   * @private
   * @see customCallback
   */
  private createTileCallback(point: BacnetPoint, tileType: TileType) {
    const writable = point.Type.includes('Output');
    const tile = makeTileFromBacnetPoint(point, tileType, writable, this.selectedDevice);

    this.tilesService.createTile$(tile).subscribe({
      next: (tileId) => {
        // Store metadata in background - don't block UI
        this.storeMetadataInBackground(tileId, point);
        this.editingTile = tileId;
      },
      error: (err) => {
        const message = err.status === 403
          ? 'You do not have permission to create tiles on this hub or device.'
          : 'Failed to create tile. Please try again.';
        this.shadeTools.errorDialog(message);
      }
    });
  }

  private storeMetadataInBackground(tileId: TileId, point: BacnetPoint): void {
    const metadata = {
      discovery: {
        name: point.Name,
        description: point.Description,
        type: point.Type,
        address: point.Address,
        units: this.getUnitsString(point.UnitsState),
        pointId: point.ID
      }
    };

    this.metadataService.patchPermanentMetadata$(tileId, metadata).subscribe({
      error: (err) => console.error('Failed to store metadata for tile', tileId, err)
    });
  }


  /**
   *
   * @param timeout
   */
  updateTimeout(timeout: Option): void {
    this.timeout = +timeout.id;
  }


  /**
   *
   */
  startDiscovery(): void {
    if (this.discovering) {
      return;
    }

    if (!this.selectedDevice) {
      this.shadeTools.errorDialog('Select a device first and then try again.');
      return;
    }

    this.discovering = true;

    const device = this.devices.find((d) => d.ID === this.selectedDevice);
    if (!device) {
      this.discovering = false;
      this.shadeTools.errorDialog('Selected device not found. Please select a valid device and try again.');
      return;
    }

    this.discoverySub = this.pointsService.discoverPoints$(device.ID, this.timeout).subscribe(
      (points) => {
        this.discovering = false;
        this.points = points;
        this.filterAndRenderPoints();
      },
      () => {
        this.discovering = false;
        this.filterAndRenderPoints();
        this.shadeTools.simpleOkDialog('Discovery failed', 'An error was encountered while attempting point discovery. Verify the device is configured correctly and then try again.');
      },
    );
  }


  /**
   *
   */
  stopDiscovery(): void {
    this.discoverySub?.unsubscribe();
    this.discovering = false;
  }


  /**
   *
   * @param text
   */
  infoDialog(text: string): void {
    this.shadeTools.infoDialog(text);
  }


  /**
   *
   * @param units
   */
  private getUnitsString(units: string | string[]): string {
    if (Array.isArray(units)) {
      return units.join(' • ');
    }

    return bacnet.makeFriendlyUnits(units);
  }

  isMobile(): boolean {
    return isMobileDevice();
  }

  /**
   * Exports the filtered points to CSV format and triggers download
   */
  canExportCSV(): boolean {
    return this.filteredPoints.length > 0;
  }

  exportToCSV(): void {
    if (!this.canExportCSV()) {
      this.shadeTools.infoDialog('No points to export. Please discover points first.');
      return;
    }

    try {
      // Prepare data for CSV export with all available fields
      const csvData = this.filteredPoints.map(point => ({
        'Name': point.Name || '',
        'Description': point.Description || '',
        'BACnet Object Type': point.Type || '',
        'BACnet Instance Number': point.Address || '',
        'Value': bacnet.makeFriendlyPointValue(point) || '',
        'Units': this.getUnitsString(point.UnitsState) || '',
        'Overridden': point.Overridden ? 'Yes' : 'No'
      }));

      // Generate CSV
      const csv = Papa.unparse(csvData);

      // Generate filename
      const deviceName = this.getCurrentSelectedDevice()?.Name || 'device';
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${deviceName}-points-${timestamp}.csv`;

      // Download using universal utility (works on web and mobile)
      FileDownloadUtil.downloadCsv(csv, filename).subscribe({
        error: (err) => this.shadeTools.errorDialog(`Failed to export CSV: ${err?.message || 'Unknown error'}`)
      });
    } catch (err: any) {
      this.shadeTools.errorDialog(`Failed to export CSV: ${err?.message || 'Unknown error'}`);
    }
  }

  hasToolsOptions(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_BACNET_BULK_CREATION);
  }

  createToolsOptions = (): OptionGroup => {
    const options: Option[] = [
      {
        id: 'export-csv',
        name: 'Export to CSV',
        icon: 'arrow-down-to-bracket',
        callback: () => this.exportToCSV(),
      }
    ];

    // Only show bulk tile creation if feature flag is enabled
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_BACNET_BULK_CREATION)) {
      options.push({
        id: 'bulk-creation',
        name: 'Bulk Tile Creation',
        icon: 'layer-group',
        callback: () => this.openBulkTileCreation(),
      });
    }

    return new OptionGroup(options);
  };

  openBulkTileCreation(): void {
    const deviceId = this.selectedDevice;
    const points = this.points;

    // Open the bulk creation modal using the new service
    this.bulkModalService.openCreationModal({
      deviceId,
      points,
      protocol: DeviceType.BACnet
    });
  }



  timeoutOptions: OptionGroup = new OptionGroup([
    {
      id: '20',
      name: '20 seconds',
      callback: () => {
        this.timeout = 20;
      },
      selected: true,
    },
    {
      id: '40',
      name: '40 seconds',
      callback: () => {
        this.timeout = 40;
      },
      selected: false,
    },
    {
      id: '60',
      name: '1 minute',
      callback: () => {
        this.timeout = 60;
      },
      selected: false,
    },
    {
      id: '120',
      name: '2 minutes',
      callback: () => {
        this.timeout = 120;
      },
      selected: false,
    },
    {
      id: '300',
      name: '5 minutes',
      callback: () => {
        this.timeout = 300;
      },
      selected: false,
    },
    {
      id: '600',
      name: '10 minutes',
      callback: () => {
        this.timeout = 600;
      },
      selected: false,
    },
  ]);
}
